import { FC, useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import styles from './Filters.module.scss';
import Dropdown from '../Pagination/Dropdown';
import FiltersIcon from '../../../../Icons/Filters';
import { CrossIcon } from '../../../../Icons/Cross';
import Arrow from '../../../../BackButton/Arrow';

export interface IFiltersItem {
  name: string;
  label: string;
  options: {
    name: string;
    label: string;
  }[];
  active?: string | null;
  clear: boolean;
  //eslint-disable-next-line
  updateOption: (name: string, option: string) => void;
  closeModal?: () => void;
}

export interface IFiltersProps {
  data: {
    name: string;
    label: string;
    options: {
      name: string;
      label: string;
    }[];
    active?: string | null;
  }[];
  //eslint-disable-next-line
  updateFilters?: (filters: {
    type: string;
    category: string;
    brand: string;
  }) => void;
}

const FiltersItem: FC<IFiltersItem> = ({
  name,
  label,
  options,
  clear,
  updateOption,
  active,
  closeModal,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeOption, setActiveOption] = useState<string>(active || '');
  const modalRef = useRef<HTMLDivElement | null>(null);

  const onClickOption = (name: string, value: string) => {
    updateOption(name, value);
    setIsOpen(false);
    setActiveOption(value);
  };

  useEffect(() => {
    if (clear) setActiveOption('');
  }, [clear]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node))
        setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      className={clsx(styles.FiltersItem, {
        [styles.open]: isOpen,
        [styles.long]: name === 'category',
        [styles.active]: options.find((item) => item.name == activeOption)
          ?.label,
      })}
      ref={modalRef}
    >
      <div
        className={styles.select}
        onClick={() => (isOpen ? setIsOpen(false) : setIsOpen(true))}
      >
        <span className={styles.filter_name}>
          {options.find((item) => item.name == '')?.label || label}
        </span>
        <span>
          {options.find((item) => item.name == activeOption)?.label || label}
        </span>
        <Dropdown />
      </div>
      <div className={styles.options}>
        {closeModal ? (
          <div className={styles.popup_header}>
            <button
              className={styles.back_btn}
              onClick={() => setIsOpen(false)}
            >
              <Arrow />
            </button>
            <h3>{options.find((item) => item.name == '')?.label || label}</h3>
            <button className={styles.close_btn} onClick={() => closeModal()}>
              <CrossIcon />
            </button>
          </div>
        ) : null}

        <ul>
          {options.map((item, index) => (
            <li key={index} onClick={() => onClickOption(name, item.name)}>
              {name === 'type' ? (
                <img
                  src={
                    item.name === 'products'
                      ? '/icons/model.png'
                      : item.name === 'part'
                        ? '/icons/detail.png'
                        : '/icons/accessory.png'
                  }
                />
              ) : null}
              {item.label}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const Filters: FC<IFiltersProps> = ({ data, updateFilters }) => {
  const [type, setType] = useState<string>('');
  const [category, setCategory] = useState<string>(
    data.find((item) => item.name === 'category')?.active || '',
  );
  const [brand, setBrand] = useState<string>('');

  const [empty, setEmpty] = useState<boolean>(true);
  const [clearState, setClearState] = useState<boolean>(false);

  const updateOption = (name: string, value: string) => {
    if (name === 'type') setType(value);
    if (name === 'category') setCategory(value);
    if (name === 'brand') setBrand(value);
    setClearState(false);
  };

  const clear = () => {
    setType('');
    setCategory('');
    setBrand('');
    setClearState(true);
  };

  useEffect(() => {
    if (updateFilters)
      updateFilters({ type: type, category: category, brand: brand });

    if (brand == '' && category == '' && type == '') setEmpty(true);
    else setEmpty(false);
  }, [type, category, brand]);

  const [isOpenModal, setIsOpenModal] = useState(false);

  const toggleModal = () => {
    if (isOpenModal) setIsOpenModal(false);
    else setIsOpenModal(true);
  };

  return (
    <div className={styles.Filters}>
      {data.map((item, index) => (
        <FiltersItem
          label={item.label}
          name={item.name}
          options={item.options}
          updateOption={updateOption}
          clear={clearState}
          key={index}
          active={item.active}
        />
      ))}
      {!empty && (
        <button className={styles.clear_btn} onClick={() => clear()}>
          Очистить все
        </button>
      )}
      <button
        className={clsx(styles.mobile_btn, { [styles.active]: !empty })}
        onClick={() => toggleModal()}
      >
        <FiltersIcon />
      </button>
      <div
        className={clsx(styles.filters_popup, { [styles.open]: isOpenModal })}
      >
        <div className={styles.popup_header}>
          <h3>Фильтры</h3>
          <button className={styles.close_btn} onClick={() => toggleModal()}>
            <CrossIcon />
          </button>
        </div>
        <div className={styles.popup_content}>
          {data.map((item, index) => (
            <FiltersItem
              label={item.label}
              name={item.name}
              options={item.options}
              updateOption={updateOption}
              clear={clearState}
              key={index}
              active={item.active}
              closeModal={toggleModal}
            />
          ))}
        </div>
        <div className={styles.popup_footer}>
          {!empty && (
            <>
              <button
                className={styles.popup_apply_btn}
                onClick={() => toggleModal()}
              >
                Применить
              </button>
              <button
                className={styles.popup_clear_btn}
                onClick={() => {
                  clear();
                  toggleModal();
                }}
              >
                Очистить все
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Filters;
